import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

interface LeaveModalProps {
  open: boolean
  learnerName: string
  pending: boolean
  onConfirm: (body: { leaveStart: string; leaveEnd?: string; reason?: string }) => void
  onClose: () => void
}

/** Leave modal — start date (defaults to today), optional end date and reason */
export function LeaveModal({ open, learnerName, pending, onConfirm, onClose }: LeaveModalProps) {
  const { t } = useTranslation(['admin', 'common'])
  const [leaveStart, setLeaveStart] = useState(todayIsoDate())
  const [leaveEnd, setLeaveEnd] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setLeaveStart(todayIsoDate())
      setLeaveEnd('')
      setReason('')
      setError(null)
    }
  }, [open])

  const handleConfirm = () => {
    if (leaveEnd && leaveEnd < leaveStart) {
      setError(t('admin:students.statusActions.invalidPeriod'))
      return
    }
    onConfirm({ leaveStart, leaveEnd: leaveEnd || undefined, reason: reason.trim() || undefined })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin:students.statusActions.leaveTitle', { name: learnerName })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.statusActions.leaveStart')} *
            </label>
            <input
              type="date"
              value={leaveStart}
              onChange={(e) => setLeaveStart(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.statusActions.leaveEnd')}
            </label>
            <input
              type="date"
              value={leaveEnd}
              onChange={(e) => setLeaveEnd(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.statusActions.reason')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={pending}
            className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:bg-gray-300"
          >
            {t('admin:students.statusActions.leaveConfirm')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface WithdrawModalProps {
  open: boolean
  learnerName: string
  pending: boolean
  onConfirm: (body: { reason?: string }) => void
  onClose: () => void
}

/** Withdraw modal — warns about bulk enrollment cancel + login block, optional reason */
export function WithdrawModal({ open, learnerName, pending, onConfirm, onClose }: WithdrawModalProps) {
  const { t } = useTranslation(['admin', 'common'])
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin:students.statusActions.withdrawTitle', { name: learnerName })}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-gray-700 whitespace-pre-line">
            {t('admin:students.statusActions.withdrawWarning')}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin:students.statusActions.reason')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            {t('common:button.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ reason: reason.trim() || undefined })}
            disabled={pending}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300"
          >
            {t('admin:students.statusActions.withdrawConfirm')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
